import React, { useState, useEffect } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Button,
  Input,
  Textarea,
  Select,
  SelectItem,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure,
  Chip,
  Divider,
  Switch,
  Badge,
  Tooltip,
  Spinner,
} from "@nextui-org/react";
import {
  Plus,
  Edit,
  Trash2,
  Eye,
  Settings,
  Copy,
  CheckCircle,
  AlertCircle,
} from "lucide-react";
import { toast } from "react-hot-toast";
import {
  getDataWithFilterUrl,
  PublishData,
  UpdateOneData,
  DeleteOnedata,
} from "../../controllers/APIController";
import {
  StandardPrimaryButton,
  StandardSecondaryButton,
  StandardCancelButton,
  StandardDeleteButton,
} from "../../components/StandardButtons";
import { useQuery, useQueryClient } from "@tanstack/react-query";
const WorkflowManagement = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [workflows, setWorkflows] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingWorkflow, setEditingWorkflow] = useState(null);
  const userLogged = JSON.parse(sessionStorage.getItem("userData"));
  const jwt = userLogged?.jwt;
  const [formData, setFormData] = useState({
    Name: "",
    Description: "",
    workflowSteps: [],
    businessRules: {},
    RequiresNotification: true,
    AutoAssign: true,
    IsActive: true,
  });
  const [errors, setErrors] = useState({});
  const queryClient = useQueryClient();

  const { data: workflowData,isPending} = useQuery({
    queryKey: ["workflows"],
    queryFn: () => getDataWithFilterUrl("workflows"),
  });

  const { data: userData } = useQuery({
    queryKey: ["users", jwt],
    queryFn: () => getDataWithFilterUrl("users", jwt),
  });

  useEffect(() => {
    if (workflowData?.success) {
      setWorkflows(workflowData?.data || []);
    }
    if (userData?.success && userData?.data) {
      const mappedUsers = userData?.data?.map((user) => ({
        ...user,
        displayName:
          user.firstName && user.lastName
            ? `${user.firstName} ${user.lastName}`
            : user.username || user.email || `User ${user.id}`,
      }));
      setUsers(mappedUsers);
    } else {
      setUsers([]);
    }
  }, [workflowData, userData]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const addWorkflowStep = () => {
    const newStep = {
      level: formData.workflowSteps.length + 1,
      type: "approve",
      description: "",
      required: true,
      estimatedDays: 3,
      canReject: true,
      canReturn: true,
      notifications: true,
    };

    setFormData((prev) => ({
      ...prev,
      workflowSteps: [...prev.workflowSteps, newStep],
    }));
  };

  const updateWorkflowStep = (index, field, value) => {
    const updatedSteps = [...formData.workflowSteps];
    updatedSteps[index] = {
      ...updatedSteps[index],
      [field]: value,
    };

    // Update level numbers if needed
    if (field === "type" && value === "skip") {
      updatedSteps[index].required = false;
    }

    setFormData((prev) => ({
      ...prev,
      workflowSteps: updatedSteps,
    }));
  };

  const removeWorkflowStep = (index) => {
    const updatedSteps = formData.workflowSteps?.filter((_, i) => i !== index);

    // Recalculate levels
    const recalculatedSteps = updatedSteps?.map((step, i) => ({
      ...step,
      level: i + 1,
    }));

    setFormData((prev) => ({
      ...prev,
      workflowSteps: recalculatedSteps,
    }));
  };

  const addBusinessRule = () => {
    const newRule = {
      name: "",
      description: "",
      condition: "",
      action: "",
      priority: 1,
    };

    setFormData((prev) => ({
      ...prev,
      businessRules: {
        ...prev.businessRules,
        [Date.now()]: newRule,
      },
    }));
  };

  const updateBusinessRule = (ruleId, field, value) => {
    setFormData((prev) => ({
      ...prev,
      businessRules: {
        ...prev.businessRules,
        [ruleId]: {
          ...prev.businessRules[ruleId],
          [field]: value,
        },
      },
    }));
  };

  const removeBusinessRule = (ruleId) => {
    setFormData((prev) => {
      const newRules = { ...prev.businessRules };
      delete newRules[ruleId];
      return { ...prev, businessRules: newRules };
    });
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.Name.trim()) {
      newErrors.Name = "Workflow name is required";
    }

    if (!formData.Description.trim()) {
      newErrors.Description = "Workflow description is required";
    }

    if (formData.workflowSteps.length === 0) {
      newErrors.workflowSteps = "At least one workflow step is required";
      return false; // Early return to prevent further validation
    }

    // Validate each workflow step
    formData.workflowSteps?.forEach((step, index) => {
      if (!step.description?.trim()) {
        newErrors[
          `step${index}Description`
        ] = `Step ${step.level} description is required`;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      // If no workflow steps, offer to add one automatically
      if (formData?.workflowSteps?.length === 0) {
        const shouldAddStep = window.confirm(
          "No workflow steps defined. Would you like to add a default workflow step automatically?"
        );
        if (shouldAddStep) {
          addWorkflowStep();
          toast.success(
            "Default workflow step added. Please fill in the details and try again."
          );
          return;
        } else {
          toast.error(
            "Please add at least one workflow step before creating the workflow."
          );
          return;
        }
      }

      toast.error("Please fix the form errors");
      return;
    }

    try {
      setLoading(true);

      if (editingWorkflow) {
        await UpdateOneData("workflows", editingWorkflow.id, formData);

        toast.success("Workflow updated successfully");
      } else {
        await PublishData(formData, "workflows");
        toast.success("Workflow created successfully");
      }

      onClose();
      resetForm();
      queryClient.invalidateQueries({ queryKey: ["workflows"] });
    } catch (error) {
      console.error("Error saving workflow:", error);
      toast.error("Failed to save workflow");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (workflow) => {
    setEditingWorkflow(workflow);
    setFormData({
      Name: workflow.Name || "",
      Description: workflow.Description || "",
      workflowSteps: workflow.workflowSteps || [],
      businessRules: workflow.businessRules || {},
      RequiresNotification: workflow.RequiresNotification ?? true,
      AutoAssign: workflow.AutoAssign ?? true,
      IsActive: workflow.IsActive ?? true,
    });
    setErrors({});
    onOpen();
  };

  const handleDelete = async (workflowId) => {
    if (
      window.confirm(
        "Are you sure you want to delete this workflow? This action cannot be undone."
      )
    ) {
      try {
        await DeleteOnedata("workflows", workflowId);
        queryClient.invalidateQueries({ queryKey: ["workflows"] });
        toast.success("Workflow deleted successfully");
      } catch (error) {
        console.error("Error deleting workflow:", error);
        toast.error("Failed to delete workflow");
      }
    }
  };

  const handleDuplicate = (workflow) => {
    setEditingWorkflow(null);
    setFormData({
      Name: `${workflow.Name} (Copy)`,
      Description: workflow.Description || "",
      workflowSteps: workflow.workflowSteps || [],
      businessRules: workflow.businessRules || {},
      RequiresNotification: workflow.RequiresNotification ?? true,
      AutoAssign: workflow.AutoAssign ?? true,
      IsActive: false, // Start as inactive for safety
    });
    setErrors({});
    onOpen();
  };

  const resetForm = () => {
    setFormData({
      Name: "",
      Description: "",
      workflowSteps: [],
      businessRules: {},
      RequiresNotification: true,
      AutoAssign: true,
      IsActive: true,
    });
    setEditingWorkflow(null);
    setErrors({});
  };

  const getWorkflowStepTypeLabel = (type) => {
    const labels = {
      review: "Review",
      approve: "Approve",
      final_approve: "Final Approve",
      skip: "Skip (Optional)",
    };
    return labels[type] || type;
  };

  const getWorkflowStepTypeColor = (type) => {
    const colors = {
      review: "primary",
      approve: "success",
      final_approve: "warning",
      skip: "default",
    };
    return colors[type] || "default";
  };

  const getWorkflowStatusColor = (isActive) => {
    return isActive ? "success" : "default";
  };

  const getWorkflowStatusText = (isActive) => {
    return isActive ? "Active" : "Inactive";
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-800">
            Workflow Management
          </h1>
          <p className="text-slate-600 mt-2">
            Create and manage approval workflow templates for tasks
          </p>
        </div>
        <StandardPrimaryButton
          startContent={<Plus size={20} />}
          onPress={() => {
            resetForm();
            onOpen();
          }}
        >
          Create Workflow
        </StandardPrimaryButton>
      </div>

      {/* Workflows Grid */}
      {isPending ? (
        <div className="flex justify-center items-center py-12">
          <Spinner size="lg" color="primary" />
          <span className="ml-3 text-slate-600">Loading workflows...</span>
        </div>
      ) : workflows?.length === 0 ? (
        <Card className="border border-slate-200">
          <CardBody className="text-center py-12">
            <div className="text-slate-400 mb-4">
              <Settings size={48} className="mx-auto" />
            </div>
            <h3 className="text-lg font-semibold text-slate-600 mb-2">
              No Workflows Yet
            </h3>
            <p className="text-slate-500 mb-4">
              Create your first workflow to get started with task approval
              processes.
            </p>
            <StandardPrimaryButton
              startContent={<Plus size={16} />}
              onPress={() => {
                resetForm();
                onOpen();
              }}
            >
              Create First Workflow
            </StandardPrimaryButton>
          </CardBody>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {workflows?.map((workflow) => (
            <Card
              key={workflow.id}
              className="border border-slate-200 hover:shadow-md transition-shadow"
            >
              <CardHeader className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-lg font-semibold text-slate-800">
                      {workflow.Name}
                    </h3>
                    <Badge
                      color={getWorkflowStatusColor(workflow.IsActive)}
                      variant="flat"
                      size="sm"
                    >
                      {getWorkflowStatusText(workflow.IsActive)}
                    </Badge>
                  </div>
                  <p className="text-sm text-slate-600">
                    {workflow.Description}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Tooltip content="Edit Workflow">
                    <StandardSecondaryButton
                      isIconOnly
                      size="sm"
                      onPress={() => handleEdit(workflow)}
                    >
                      <Edit size={16} />
                    </StandardSecondaryButton>
                  </Tooltip>
                  <Tooltip content="Duplicate Workflow">
                    <StandardSecondaryButton
                      isIconOnly
                      size="sm"
                      onPress={() => handleDuplicate(workflow)}
                    >
                      <Copy size={16} />
                    </StandardSecondaryButton>
                  </Tooltip>
                  <Tooltip content="Delete Workflow">
                    <StandardDeleteButton
                      isIconOnly
                      size="sm"
                      onPress={() => handleDelete(workflow.id)}
                    >
                      <Trash2 size={16} />
                    </StandardDeleteButton>
                  </Tooltip>
                </div>
              </CardHeader>
              <CardBody>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">
                      Workflow Steps:
                    </span>
                    <Chip color="primary" variant="flat" size="sm">
                      {workflow.workflowSteps?.length || 0}
                    </Chip>
                  </div>

                  {workflow.workflowSteps &&
                    workflow.workflowSteps.length > 0 && (
                      <div>
                        <span className="text-sm text-slate-600 block mb-2">
                          Workflow Steps:
                        </span>
                        <div className="space-y-2">
                          {workflow.workflowSteps
                            ?.slice(0, 3)
                            ?.map((step, index) => (
                              <div
                                key={index}
                                className="flex items-center gap-2"
                              >
                                <Chip
                                  color={getWorkflowStepTypeColor(step.type)}
                                  variant="flat"
                                  size="sm"
                                >
                                  {step.level}:{" "}
                                  {getWorkflowStepTypeLabel(step.type)}
                                </Chip>
                                {step.required && (
                                  <Chip
                                    color="warning"
                                    variant="flat"
                                    size="sm"
                                  >
                                    Required
                                  </Chip>
                                )}
                              </div>
                            ))}
                          {workflow.workflowSteps.length > 3 && (
                            <Chip color="default" variant="flat" size="sm">
                              +{workflow.workflowSteps.length - 3} more
                            </Chip>
                          )}
                        </div>
                      </div>
                    )}

                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">
                      Notifications:
                    </span>
                    <Chip
                      color={
                        workflow.RequiresNotification ? "success" : "default"
                      }
                      variant="flat"
                      size="sm"
                    >
                      {workflow.RequiresNotification ? "Enabled" : "Disabled"}
                    </Chip>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">Auto-Assign:</span>
                    <Chip
                      color={workflow.AutoAssign ? "success" : "default"}
                      variant="flat"
                      size="sm"
                    >
                      {workflow.AutoAssign ? "Enabled" : "Disabled"}
                    </Chip>
                  </div>
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Workflow Modal */}
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="5xl"
        scrollBehavior="inside"
      >
        <ModalContent>
          <ModalHeader>
            <div className="flex items-center gap-3">
              {editingWorkflow ? (
                <>
                  <Edit className="w-5 h-5 text-emerald-600" />
                  <h2 className="text-xl font-semibold">Edit Workflow</h2>
                </>
              ) : (
                <>
                  <Plus className="w-5 h-5 text-emerald-600" />
                  <h2 className="text-xl font-semibold">Create New Workflow</h2>
                </>
              )}
            </div>
          </ModalHeader>
          <ModalBody>
            <div className="space-y-6">
              {/* Basic Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-slate-700 border-b border-slate-200 pb-2">
                  Basic Information
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Workflow Name"
                    placeholder="Enter workflow name"
                    value={formData.Name}
                    onChange={(e) => handleInputChange("Name", e.target.value)}
                    isInvalid={!!errors.Name}
                    errorMessage={errors.Name}
                    isRequired
                    variant="bordered"
                  />
                  <div className="flex items-center justify-center p-4 border border-slate-200 rounded-lg">
                    <div className="text-center">
                      <p className="text-sm font-medium text-slate-700">
                        Workflow Steps
                      </p>
                      <p className="text-xs text-slate-500">
                        Add steps below to define approval levels
                      </p>
                    </div>
                  </div>
                </div>

                <Textarea
                  label="Description"
                  placeholder="Describe the workflow purpose and process"
                  value={formData.Description}
                  onChange={(e) =>
                    handleInputChange("Description", e.target.value)
                  }
                  isInvalid={!!errors.Description}
                  errorMessage={errors.Description}
                  rows={3}
                  variant="bordered"
                  isRequired
                />
              </div>

              {/* Workflow Steps */}
              <div className="space-y-4">
                <div className="flex justify-between items-center border-b border-slate-200 pb-2">
                  <h3 className="text-lg font-semibold text-slate-700">
                    Workflow Steps
                  </h3>
                  <div className="flex items-center gap-2">
                    {errors.workflowSteps && (
                      <Chip
                        color="danger"
                        variant="flat"
                        size="sm"
                        startContent={<AlertCircle size={14} />}
                      >
                        {errors.workflowSteps}
                      </Chip>
                    )}
                    <Button
                      size="sm"
                      color="primary"
                      onPress={addWorkflowStep}
                      startContent={<Plus size={16} />}
                      className="bg-emerald-600 hover:bg-emerald-700"
                    >
                      Add Step
                    </Button>
                  </div>
                </div>

                {formData.workflowSteps.length === 0 ? (
                  <Card className="border border-dashed border-slate-300">
                    <CardBody className="text-center py-8">
                      <div className="text-slate-400 mb-2">
                        <Settings size={32} className="mx-auto" />
                      </div>
                      <p className="text-slate-500">
                        No workflow steps defined yet
                      </p>
                      <p className="text-sm text-slate-400">
                        Add steps to define your approval process
                      </p>
                    </CardBody>
                  </Card>
                ) : (
                  <div className="space-y-4">
                    {formData.workflowSteps.map((step, index) => (
                      <Card key={index} className="border border-slate-200">
                        <CardBody>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                            <div>
                              <label className="text-sm font-medium text-slate-700">
                                Level {step.level}
                              </label>
                            </div>

                            <Select
                              label="Step Type"
                              selectedKeys={[step.type]}
                              onSelectionChange={(keys) =>
                                updateWorkflowStep(
                                  index,
                                  "type",
                                  Array.from(keys)[0]
                                )
                              }
                              size="sm"
                              variant="bordered"
                            >
                              <SelectItem key="review" value="review">
                                Review
                              </SelectItem>
                              <SelectItem key="approve" value="approve">
                                Approve
                              </SelectItem>
                              <SelectItem
                                key="final_approve"
                                value="final_approve"
                              >
                                Final Approve
                              </SelectItem>
                              <SelectItem key="skip" value="skip">
                                Skip (Optional)
                              </SelectItem>
                            </Select>

                            <Input
                              label="Description"
                              placeholder="Step description"
                              value={step.description}
                              onChange={(e) =>
                                updateWorkflowStep(
                                  index,
                                  "description",
                                  e.target.value
                                )
                              }
                              isInvalid={!!errors[`step${index}Description`]}
                              errorMessage={errors[`step${index}Description`]}
                              size="sm"
                              variant="bordered"
                            />

                            <div className="flex gap-2">
                              <Input
                                label="Est. Days"
                                placeholder="Days"
                                type="number"
                                value={step.estimatedDays}
                                onChange={(e) =>
                                  updateWorkflowStep(
                                    index,
                                    "estimatedDays",
                                    parseInt(e.target.value) || 0
                                  )
                                }
                                min={1}
                                max={30}
                                size="sm"
                                variant="bordered"
                              />
                              <Button
                                isIconOnly
                                size="sm"
                                color="danger"
                                variant="light"
                                onPress={() => removeWorkflowStep(index)}
                              >
                                <Trash2 size={16} />
                              </Button>
                            </div>
                          </div>

                          <div className="flex gap-4 mt-3">
                            <Switch
                              isSelected={step.required}
                              onValueChange={(value) =>
                                updateWorkflowStep(index, "required", value)
                              }
                              size="sm"
                            >
                              Required Step
                            </Switch>

                            <Switch
                              isSelected={step.canReject}
                              onValueChange={(value) =>
                                updateWorkflowStep(index, "canReject", value)
                              }
                              size="sm"
                            >
                              Can Reject
                            </Switch>

                            <Switch
                              isSelected={step.canReturn}
                              onValueChange={(value) =>
                                updateWorkflowStep(index, "canReturn", value)
                              }
                              size="sm"
                            >
                              Can Return
                            </Switch>

                            <Switch
                              isSelected={step.notifications}
                              onValueChange={(value) =>
                                updateWorkflowStep(
                                  index,
                                  "notifications",
                                  value
                                )
                              }
                              size="sm"
                            >
                              Notifications
                            </Switch>
                          </div>
                        </CardBody>
                      </Card>
                    ))}
                  </div>
                )}
              </div>

              {/* Business Rules */}
              <div className="space-y-4">
                <div className="flex justify-between items-center border-b border-slate-200 pb-2">
                  <h3 className="text-lg font-semibold text-slate-700">
                    Business Rules
                  </h3>
                  <Button
                    size="sm"
                    color="primary"
                    onPress={addBusinessRule}
                    startContent={<Plus size={16} />}
                    className="bg-emerald-600 hover:bg-emerald-700"
                  >
                    Add Rule
                  </Button>
                </div>

                {Object.keys(formData.businessRules).length === 0 ? (
                  <Card className="border border-dashed border-slate-300">
                    <CardBody className="text-center py-6">
                      <p className="text-slate-500">
                        No business rules defined
                      </p>
                      <p className="text-sm text-slate-400">
                        Add rules to define conditional workflow behavior
                      </p>
                    </CardBody>
                  </Card>
                ) : (
                  <div className="space-y-4">
                    {Object.entries(formData.businessRules).map(
                      ([ruleId, rule]) => (
                        <Card key={ruleId} className="border border-slate-200">
                          <CardBody>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <Input
                                label="Rule Name"
                                placeholder="Rule name"
                                value={rule.name}
                                onChange={(e) =>
                                  updateBusinessRule(
                                    ruleId,
                                    "name",
                                    e.target.value
                                  )
                                }
                                size="sm"
                                variant="bordered"
                              />

                              <Input
                                label="Priority"
                                placeholder="Priority (1-10)"
                                type="number"
                                value={rule.priority}
                                onChange={(e) =>
                                  updateBusinessRule(
                                    ruleId,
                                    "priority",
                                    parseInt(e.target.value) || 0
                                  )
                                }
                                min={1}
                                max={10}
                                size="sm"
                                variant="bordered"
                              />
                            </div>

                            <Textarea
                              label="Description"
                              placeholder="Rule description"
                              value={rule.description}
                              onChange={(e) =>
                                updateBusinessRule(
                                  ruleId,
                                  "description",
                                  e.target.value
                                )
                              }
                              rows={2}
                              className="mt-3"
                              variant="bordered"
                            />

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                              <Input
                                label="Condition"
                                placeholder="When this rule applies"
                                value={rule.condition}
                                onChange={(e) =>
                                  updateBusinessRule(
                                    ruleId,
                                    "condition",
                                    e.target.value
                                  )
                                }
                                size="sm"
                                variant="bordered"
                              />

                              <Input
                                label="Action"
                                placeholder="What happens when rule is triggered"
                                value={rule.action}
                                onChange={(e) =>
                                  updateBusinessRule(
                                    ruleId,
                                    "action",
                                    e.target.value
                                  )
                                }
                                size="sm"
                                variant="bordered"
                              />
                            </div>

                            <div className="flex justify-end mt-3">
                              <StandardDeleteButton
                                size="sm"
                                onPress={() => removeBusinessRule(ruleId)}
                                startContent={<Trash2 size={16} />}
                              >
                                Remove Rule
                              </StandardDeleteButton>
                            </div>
                          </CardBody>
                        </Card>
                      )
                    )}
                  </div>
                )}
              </div>

              {/* Settings */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-slate-700 border-b border-slate-200 pb-2">
                  Settings
                </h3>

                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 border border-slate-200 rounded-lg">
                    <div>
                      <label className="text-sm font-medium text-slate-700">
                        Send Notifications
                      </label>
                      <p className="text-xs text-slate-500">
                        Enable email notifications for workflow events
                      </p>
                    </div>
                    <Switch
                      isSelected={formData.RequiresNotification}
                      onValueChange={(value) =>
                        handleInputChange("RequiresNotification", value)
                      }
                    />
                  </div>

                  <div className="flex items-center justify-between p-4 border border-slate-200 rounded-lg">
                    <div>
                      <label className="text-sm font-medium text-slate-700">
                        Auto-Assign Tasks
                      </label>
                      <p className="text-xs text-slate-500">
                        Automatically assign tasks to approvers
                      </p>
                    </div>
                    <Switch
                      isSelected={formData.AutoAssign}
                      onValueChange={(value) =>
                        handleInputChange("AutoAssign", value)
                      }
                    />
                  </div>

                  <div className="flex items-center justify-between p-4 border border-slate-200 rounded-lg">
                    <div>
                      <label className="text-sm font-medium text-slate-700">
                        Active Workflow
                      </label>
                      <p className="text-xs text-slate-500">
                        Enable this workflow for use
                      </p>
                    </div>
                    <Switch
                      isSelected={formData.IsActive}
                      onValueChange={(value) =>
                        handleInputChange("IsActive", value)
                      }
                    />
                  </div>
                </div>
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <StandardCancelButton onPress={onClose}>
              Cancel
            </StandardCancelButton>
            <StandardPrimaryButton
              onPress={handleSubmit}
              isLoading={loading}
              startContent={
                loading ? (
                  <Spinner size="sm" />
                ) : editingWorkflow ? (
                  <Edit size={16} />
                ) : (
                  <Plus size={16} />
                )
              }
            >
              {editingWorkflow ? "Update Workflow" : "Create Workflow"}
            </StandardPrimaryButton>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
};

export default WorkflowManagement;
